import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-reimagined/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import { formatCurrency } from 'tio-reimagined/format';
import PageHero from 'tio-reimagined/components/page-hero';
import StatCard from 'tio-reimagined/components/stat-card';
import SectionHeader from 'tio-reimagined/components/section-header';
import {
  PhClockCounterClockwise,
  PhCurrencyDollar,
  PhTrendUp,
  PhCheckCircle,
  PhArrowLeft,
  PhHandshake,
  PhBank,
  PhSparkle,
  PhArrowRight,
  PhSwap,
  PhFilePdf,
} from 'ember-phosphor-icons';
import PaymentStatusCard from 'tio-employee/components/reimagined/payment-status-card';
import ContributionTimeline from 'tio-employee/components/reimagined/contribution-timeline';
import PaymentAlertCard from 'tio-employee/components/reimagined/payment-alert-card';
import AccountSelectorDrawer from 'tio-employee/components/reimagined/account-selector-drawer';
import PdfExportDrawer from 'tio-employee/components/reimagined/pdf-export-drawer';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import type {
  ContributionHistoryData,
  DataSourceInfo,
} from 'tio-employee/services/reimagined-data';
import type { EmployerContributions } from 'tio-employee/services/career-pathways-api';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type SessionContextService from 'tio-employee/services/session-context';
import type PersonModel from 'tio-data/models/person';
import type RouterService from '@ember/routing/router-service';

interface HistoryModel {
  contributionHistory: ContributionHistoryData;
  employerContributions: {
    contributions: EmployerContributions;
    isLiveData: boolean;
  };
  dataSourceInfo: DataSourceInfo;
  person: PersonModel;
}

interface HistorySignature {
  Args: {
    model: HistoryModel;
  };
}

export default class CareerPathwaysFinanceHistory extends Component<HistorySignature> {
  @service declare reimaginedData: ReimaginedDataService;
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;

  @tracked isAccountDrawerOpen = false;
  @tracked isPdfDrawerOpen = false;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get history() {
    return this.args.model.contributionHistory;
  }

  get contributions() {
    return this.args.model.employerContributions.contributions;
  }

  get contributionsSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.args.model.employerContributions.isLiveData ? 'live' : 'placeholder';
  }

  get historySource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.history.isLiveData ? 'live' : 'placeholder';
  }

  get employerName() {
    if (this.isDemoMode) return 'TechForward Inc.';
    return (
      this.sessionContext.company?.name ||
      this.sessionContext.currentEmployee?.company?.name ||
      'Your employer'
    );
  }

  get heroSubtitle() {
    if (this.history.isSlrEligible && this.history.paymentCount > 0) {
      return `${this.employerName} has sent ${this.history.paymentCount} contributions totaling ${formatCurrency(this.history.lifetimeTotal)} to help pay down your student loans.`;
    }
    return `Track your employer contributions and payment history. ${this.employerName} helps you pay down your student loans faster.`;
  }

  get showPaymentCards() {
    return this.history.isSlrEligible;
  }

  get showAlerts() {
    return this.history.accountAlerts.length > 0 || this.history.paymentValidationErrors.length > 0;
  }

  get currentYearSubtext() {
    return `Contributions in ${currentYear()}`;
  }

  // --- Person & Account Getters ---

  get person() {
    return this.args.model.person;
  }

  get activeAccounts() {
    return this.person.activeAccounts;
  }

  get accountReceivingContributions() {
    return this.person.accountReceivingContributions;
  }

  get employeeId() {
    return this.sessionContext.currentEmployee?.id ?? '';
  }

  // --- Drawer Toggles ---

  toggleAccountDrawer = () => {
    this.isAccountDrawerOpen = !this.isAccountDrawerOpen;
  };

  togglePdfDrawer = () => {
    this.isPdfDrawerOpen = !this.isPdfDrawerOpen;
  };

  onAccountChanged = () => {
    this.router.refresh('authenticated.reimagined.finance.history');
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      {{! Back Navigation }}
      <div class="mb-2">
        <LinkTo
          @route="authenticated.reimagined.finance"
          class="tio-inline-row gap-1.5 text-sm font-medium tio-fg-muted hover:tio-fg-secondary transition-colors"
        >
          <PhArrowLeft @size={{16}} />
          Back to Finance
        </LinkTo>
      </div>

      {{! Hero Banner }}
      <DataIndicator
        @source={{this.historySource}}
        @showBadge={{true}}
        @dataSourceId="slr-contributions"
      >
        <PageHero
          @icon={{PhClockCounterClockwise}}
          @title="Contribution History"
          @subtitle={{this.heroSubtitle}}
          @gradient="brand"
          role="banner"
        >
          <:stat>
            <div class="text-4xl font-bold">{{formatCurrency this.history.lifetimeTotal}}</div>
            <Text class="text-violet-100">Lifetime Contributions</Text>
          </:stat>
        </PageHero>
      </DataIndicator>

      {{! Eligibility & Account Status }}
      {{#if this.history.isSlrEligible}}
        <div class="tio-row mt-4">
          <div class="flex items-center gap-1.5">
            <span class="w-2 h-2 rounded-full tio-bg-success"></span>
            <Text @size="sm" class="tio-fg-secondary">Receiving Contributions</Text>
          </div>
          {{#if this.history.accountReceivingName}}
            <span class="text-gray-300">·</span>
            <div class="flex items-center gap-1.5">
              <PhBank @size={{14}} class="tio-fg-disabled" />
              <Text @size="sm" class="tio-fg-secondary">{{this.history.accountReceivingName}}</Text>
            </div>
            {{#if this.history.hasMultipleAccounts}}
              <button
                type="button"
                class="text-sm font-medium tio-fg-brand hover:tio-fg-brand tio-row-extra-tight"
                {{on "click" this.toggleAccountDrawer}}
              >
                <PhSwap @size={{14}} />
                Change Account
              </button>
            {{/if}}
          {{/if}}
        </div>
      {{/if}}

      {{! Payment Alerts }}
      {{#if this.showAlerts}}
        <PaymentAlertCard
          @alerts={{this.history.accountAlerts}}
          @validationErrors={{this.history.paymentValidationErrors}}
          @isSlrEligible={{this.history.isSlrEligible}}
          class="mt-4"
        />
      {{/if}}

      {{! Last Payment / Next Payment / Totals Row }}
      {{#if this.showPaymentCards}}
        <DataIndicator
          @source={{this.historySource}}
          @showBadge={{true}}
          @dataSourceId="slr-contributions"
          class="mt-6"
        >
          <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
            {{! Last Payment }}
            {{#if this.history.lastPayment}}
              <PaymentStatusCard
                @title="Last Payment"
                @amount={{this.history.lastPayment.amount}}
                @date={{this.history.lastPayment.paidDate}}
                @status={{this.history.lastPayment.status}}
                @theme="violet"
              />
            {{/if}}

            {{! Next Payment }}
            {{#if this.history.nextPayment}}
              <PaymentStatusCard
                @title="Next Payment"
                @amount={{this.history.nextPayment.amount}}
                @date={{this.history.nextPayment.benefitCycle}}
                @status={{this.history.nextPayment.status}}
                @theme="blue"
              />
            {{/if}}

            {{! Total Stats }}
            <StatCard
              @icon={{PhCurrencyDollar}}
              @label="Year-to-Date"
              @value={{formatCurrency this.history.yearToDate}}
              @subtext={{this.currentYearSubtext}}
              @color="emerald"
            />

            <StatCard
              @icon={{PhTrendUp}}
              @label="Total Payments"
              @value={{this.history.paymentCount}}
              @subtext={{formatCurrency this.history.lifetimeTotal}}
              @color="violet"
            >
              <:extra>
                <div class="tio-row-extra-tight tio-fg-success text-sm">
                  <PhCheckCircle @size={{14}} />
                  <span>All time</span>
                </div>
              </:extra>
            </StatCard>
          </div>
        </DataIndicator>
      {{/if}}

      {{! Projected Annual Summary }}
      <DataIndicator
        @source={{this.contributionsSource}}
        @showBadge={{true}}
        @dataSourceId="slr-contributions"
        class="mt-6"
      >
        <div class="tio-bg-success-gradient rounded-xl p-6 tio-fg-inverse">
          <div class="tio-row-between flex-wrap gap-4">
            <div class="tio-row-loose">
              <div class="p-3 bg-white/20 rounded-lg">
                <PhHandshake @size={{28}} />
              </div>
              <div>
                <Heading @level={{3}} class="tio-fg-inverse">
                  {{this.employerName}}
                  Contribution Summary
                </Heading>
                <Text class="text-emerald-100">
                  Projected
                  {{formatCurrency this.contributions.projectedAnnual}}
                  this year based on current contribution rate.
                </Text>
              </div>
            </div>
            <div class="tio-row-extra-loose">
              <div class="text-center">
                <div class="text-2xl font-bold">{{formatCurrency
                    this.contributions.yearToDate
                  }}</div>
                <Text @size="sm" class="text-emerald-100">This Year</Text>
              </div>
              <div class="text-center">
                <div class="text-2xl font-bold">{{formatCurrency this.contributions.lifetime}}</div>
                <Text @size="sm" class="text-emerald-100">Lifetime</Text>
              </div>
            </div>
          </div>
        </div>
      </DataIndicator>

      {{! Payment History Timeline/Table }}
      <DataIndicator
        @source={{this.historySource}}
        @showBadge={{true}}
        @dataSourceId="slr-contributions"
        class="mt-6"
      >
        <ContributionTimeline @payments={{this.history.payments}} />
      </DataIndicator>

      {{! Quick Actions }}
      <div class="mt-6">
        <SectionHeader
          @icon={{PhSparkle}}
          @title="Quick Actions"
          @iconClass="tio-fg-brand-accent"
          @iconSize={{24}}
        />
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button type="button" class="block text-left" {{on "click" this.togglePdfDrawer}}>
            <div
              class="tio-bg-surface rounded-xl p-5 shadow-sm ring-1 ring-gray-950/[0.04] hover:shadow-md transition-shadow"
            >
              <div class="tio-row">
                <div class="p-2 tio-bg-danger-soft rounded-lg">
                  <PhFilePdf @size={{20}} class="tio-fg-danger" />
                </div>
                <div>
                  <Text @weight="medium">Export PDF</Text>
                  <Text @variant="muted" @size="sm">Download contribution report</Text>
                </div>
                <PhArrowRight @size={{16}} class="tio-fg-disabled ml-auto" />
              </div>
            </div>
          </button>

          <LinkTo @route="authenticated.reimagined.finance.optimizer" class="block">
            <div
              class="tio-bg-surface rounded-xl p-5 shadow-sm ring-1 ring-gray-950/[0.04] hover:shadow-md transition-shadow"
            >
              <div class="tio-row">
                <div class="p-2 tio-bg-success-soft rounded-lg">
                  <PhSparkle @size={{20}} class="tio-fg-success" />
                </div>
                <div>
                  <Text @weight="medium">Optimize Allocation</Text>
                  <Text @variant="muted" @size="sm">Use AI to maximize your benefit</Text>
                </div>
                <PhArrowRight @size={{16}} class="tio-fg-disabled ml-auto" />
              </div>
            </div>
          </LinkTo>

          <LinkTo @route="authenticated.reimagined.loans" class="block">
            <div
              class="tio-bg-surface rounded-xl p-5 shadow-sm ring-1 ring-gray-950/[0.04] hover:shadow-md transition-shadow"
            >
              <div class="tio-row">
                <div class="p-2 tio-bg-info-soft rounded-lg">
                  <PhBank @size={{20}} class="tio-fg-info" />
                </div>
                <div>
                  <Text @weight="medium">View Loan Accounts</Text>
                  <Text @variant="muted" @size="sm">See balances and progress</Text>
                </div>
                <PhArrowRight @size={{16}} class="tio-fg-disabled ml-auto" />
              </div>
            </div>
          </LinkTo>

          <LinkTo @route="authenticated.observability.upload" class="block">
            <div
              class="tio-bg-surface rounded-xl p-5 shadow-sm ring-1 ring-gray-950/[0.04] hover:shadow-md transition-shadow"
            >
              <div class="tio-row">
                <div class="p-2 tio-bg-brand-soft rounded-lg">
                  <PhTrendUp @size={{20}} class="tio-fg-primary" />
                </div>
                <div>
                  <Text @weight="medium">Upload Documents</Text>
                  <Text @variant="muted" @size="sm">Update your loan information</Text>
                </div>
                <PhArrowRight @size={{16}} class="tio-fg-disabled ml-auto" />
              </div>
            </div>
          </LinkTo>
        </div>
      </div>

      {{! Drawers }}
      <AccountSelectorDrawer
        @isOpen={{this.isAccountDrawerOpen}}
        @onClose={{this.toggleAccountDrawer}}
        @accounts={{this.activeAccounts}}
        @currentAccount={{this.accountReceivingContributions}}
        @personId={{this.person.id}}
        @onAccountChanged={{this.onAccountChanged}}
      />

      <PdfExportDrawer
        @isOpen={{this.isPdfDrawerOpen}}
        @onClose={{this.togglePdfDrawer}}
        @employeeId={{this.employeeId}}
      />
    </VStack>
  </template>
}

function currentYear(): number {
  return new Date().getFullYear();
}
